"use client";

import { ArrowDownCircle, ArrowUpCircle, Coins, RotateCcw } from "lucide-react";
import React from "react";

import { CreditTransaction, CreditTransactionType } from "./types";

/** A Cron renewal is an annual Automations plan's monthly refill: no charge behind it. */
const isMonthlyRefill = (
  type: CreditTransactionType,
  referenceType: CreditTransaction["reference_type"]
) => type === "subscription_renewal" && referenceType === "Cron";

export function getTransactionLabel(
  type: CreditTransactionType,
  creditAmount: number,
  referenceType?: CreditTransaction["reference_type"]
): string {
  const formatted = creditAmount.toLocaleString();
  if (isMonthlyRefill(type, referenceType)) {
    return `Monthly credits - ${formatted} credits`;
  }
  switch (type) {
    case "free_monthly_deposit":
      return `Free Credits - ${formatted} credits`;
    case "one_time_purchase":
      return `One Time Purchase - ${formatted} credits`;
    case "subscription_renewal":
      return `Subscription - ${formatted} credits`;
    case "message_send_debit":
      return `Message Send - ${formatted} credits`;
    case "cancellation_refund":
      return `Refund - ${formatted} credits`;
    case "admin_adjustment":
      return `Admin Adjustment - ${formatted} credits`;
    case "subscription_canceled":
      return `Subscription Cancelled`;
    case "subscription_upgrade":
      return `Subscription Upgrade - ${formatted} credits`;
    case "subscription_downgrade":
      return "Subscription Downgrade";
    default:
      return type;
  }
}

export function getTransactionDollarAmount(
  type: CreditTransactionType,
  creditAmount: number,
  amountPaidCents?: number | null,
  referenceType?: CreditTransaction["reference_type"]
): string | null {
  if (isMonthlyRefill(type, referenceType)) return null;
  const absCreditAmount = Math.abs(creditAmount);
  // Stripe invoice amount when the row matched one; else the legacy heuristic.
  const dollars =
    amountPaidCents != null
      ? (amountPaidCents / 100).toFixed(2)
      : absCreditAmount <= 10000
        ? "5.00"
        : "50.00";

  switch (type) {
    case "one_time_purchase":
    case "subscription_renewal":
      return `-$${dollars}`;
    case "free_monthly_deposit":
      return "$0.00";
    case "cancellation_refund":
      return `+$${dollars}`;
    case "subscription_upgrade":
      // Stripe-joined prorated charge only — never the heuristic.
      return amountPaidCents != null ? `-$${dollars}` : null;
    case "message_send_debit":
    case "admin_adjustment":
    case "subscription_downgrade":
    case "subscription_canceled":
    default:
      return null;
  }
}

export function TransactionIcon({ type }: { type: CreditTransactionType }) {
  const iconClass = "h-4 w-4 text-[#29333C]";
  let icon: React.ReactNode;

  switch (type) {
    case "subscription_renewal":
      icon = <Coins className={iconClass} />;
      break;
    case "free_monthly_deposit":
    case "one_time_purchase":
    case "subscription_upgrade":
      icon = <Coins className={iconClass} />;
      break;
    case "message_send_debit":
      icon = <ArrowUpCircle className={iconClass} />;
      break;
    case "cancellation_refund":
      icon = <RotateCcw className={iconClass} />;
      break;
    case "admin_adjustment":
    case "subscription_downgrade":
    case "subscription_canceled":
    default:
      icon = <ArrowDownCircle className={iconClass} />;
      break;
  }

  return (
    <div className="w-8 h-8 rounded-full bg-[#F2F2F3] flex items-center justify-center shrink-0">
      {icon}
    </div>
  );
}
